import {makeAutoObservable, observable} from 'mobx'
import {Lang} from 'yougile-lang'
import {logger, UUID} from 'yougile-util'
import {DataObjectId, Hub, PermissionRoleKey} from 'yougile-model'
import {Sync} from '@client/sync'
import {Backend} from '@common/backend'
import {MessageBox} from '@components/message-box'
import {createUseStore} from '@containers/hooks'
import {globals} from '@extensions/globals-base'
import {Project} from '@extensions/project'
import {UserAccess} from '@extensions/user-access'
import {getRoles} from '../company-invite-popup-store'
import {ValidType} from '../emails-input'
import {ProjectsListItem} from '../types'

interface State {
  roleForNewProject: PermissionRoleKey
  wait: boolean
}

const initState: State = {
  roleForNewProject: 'worker',
  wait: false
}

export class CompanyInviteRoleUserPopupStore {
  state: State = initState
  projects: ProjectsListItem[] = []
  name = ''

  constructor() {
    makeAutoObservable(this, {state: observable.shallow}, {autoBind: true})
  }

  syncProjects(): void {
    const projects = Project.listProjects(
      globals.getCompanyId(),
      'not-archive',
      true
    )
      .sort(Project.projectOrderComparer)
      .filter(p => UserAccess.canChangeUserInProject(p))

    this.projects = projects.map((p, idx) => {
      return {
        id: p.id,
        title: p.title,
        enabled: idx === 0,
        roles: getRoles(p),
        defaultRole: 'worker'
      }
    })
  }

  syncProject(currentProject?: Hub): void {
    if (currentProject) {
      this.projects = [currentProject].map((p, idx) => {
        return {
          id: p.id,
          title: p.title,
          enabled: idx === 0,
          roles: getRoles(p),
          defaultRole: 'worker'
        }
      })
    }
  }

  clearEmails(): void {
    this.name = ''
  }

  onDefaultRoleChanged(
    projectId: DataObjectId,
    value: PermissionRoleKey
  ): void {
    const p = this.getProject(projectId)
    if (p) {
      p.defaultRole = value
    }
  }

  setDefaultRoleForNewProject(value: PermissionRoleKey): void {
    this.state.roleForNewProject = value
  }

  onEnabledChanged(projectId: DataObjectId, value: boolean): void {
    const p = this.getProject(projectId)
    if (p) {
      p.enabled = value
    }
  }

  getProject(id: DataObjectId): ProjectsListItem | undefined {
    return this.projects?.find(p => p.id === id) ?? undefined
  }

  getProjectsToInvite(): {projectId: DataObjectId; role: PermissionRoleKey}[] {
    return this.projects
      .filter(p => p.enabled)
      .map(p => ({projectId: p.id, role: p.defaultRole}))
  }

  setEmails(value: string): void {
    this.name = value
  }

  canSubmit(): boolean {
    return !!this.name.length
  }

  getFullEmail(email: string): string {
    return `${email.trim()}@$role.yougile.com`
  }

  validateEmail(email: string): ValidType {
    const box = Sync.getCurrentSandbox()
    const users = new Set(box.getAllUsers(false).map(u => u.email))
    const fullEmail = this.getFullEmail(email)
    return users.has(fullEmail) ? 'warning' : 'success'
  }

  async onSubmit(): Promise<boolean> {
    if (this.state.wait) {
      return false
    }
    this.state.wait = true

    const projectsToInvite = this.getProjectsToInvite()

    const name = this.name
    if (!name) {
      logger.log('No name on submit')
      return false
    }

    const valid = this.validateEmail(name)
    if (valid !== 'success') {
      MessageBox.show(Lang.msg.invitePopup.emailAlreadyCreated, 'success')
      return false
    }

    const box = Sync.getCurrentSandbox()
    const userKey = Backend.loadLocal('user_key') as UUID
    try {
      const {error, uid} = await Backend.postJSON<{
        error: string
        uid: DataObjectId
      }>('/data/add-role-user', {
        ...Backend.loadCred(),
        userId: globals.currentUserId,
        companyId: box.companyId,
        key: userKey,
        name,
        projectsToInvite
      })

      const projectInvite = this.getProjectsToInvite()
      if (projectInvite) {
        for (const proj of projectInvite) {
          const pr = box.getHub(proj.projectId)
          if (pr && !UserAccess.getRoleInProject(uid, pr)) {
            UserAccess.setRoleInProject(uid, pr, proj.role)
          }
        }
      }

      if (error) {
        logger.error('Sending error', name, error)
        return false
      }

      return true
    } catch (error) {
      logger.error('Sending error', name, error)
      return false
    } finally {
      this.state.wait = false
    }
  }
}

const [CompanyInviteRoleUserPopupContext, useCompanyInviteRoleUserPopupStore] =
  createUseStore(CompanyInviteRoleUserPopupStore)

export {CompanyInviteRoleUserPopupContext, useCompanyInviteRoleUserPopupStore}
